import { Link, mergeStyleSets, Separator, Stack } from '@fluentui/react';
import { LockSolidIcon } from '@fluentui/react-icons-mdl2';
import {
  Alert,
  Button,
  Chat,
  ChevronDownIcon,
  CollapseIcon,
  ExpandIcon,
  Divider,
  Flex,
  ProviderConsumer as FluentUIThemeConsumer,
  Loader,
  SendIcon,
  Skeleton,
  Text,
} from '@fluentui/react-northstar';
import { clone, debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import EmptySVG from '../../../../svg/empty.svg';
import LockSVG from '../../../../svg/lock.svg';
import { appState, useSetState } from '../../../AppState';
import { getDateWithDay, getStatusIdByGuid, threedigitized } from '../../../tikit/ticketHelper';
import { getCachedFeature } from '../../cache/FeatureCache';
import { getCachedUser } from '../../cache/PlatformUserCache';
import { getCachedPhoto } from '../../cache/UserPhoto';
import { AsyncQueue } from '../../common/AsyncQueue';
import { attachmentLocation } from '../../utils/attachmentLocation';
import { CommonHelper } from '../../common/CommonHelper';
import { platformService } from '../../services/platform.service';
import { uploadService } from '../../services/upload.service';
import { ACTIVITY_ACTION, ACTIVITY_FIELD, LICENSE_FEATURE, markdownComponents, STATUS_GUID, TicketApprovalState, WARNING_NOTE } from '../../utils/constants';
import { CheckFeature } from '../CheckFeature';
import { generateCircleImage } from '../CircleImages';
import { FileProps } from '../EditTicketAttachments/EditTicketAttachments';
import { IRelatedTickets, RelationType } from '../EditTicketRelateTickets/RelateTicketHelper';
import { EmptyData } from '../EmptyData';
import { AddOrUpdateResolutionToDraftKB, IResolution } from '../TicketResolutionNoteComponents/ResolutionHelper';
import { ResolutionNoteDialog } from '../TicketResolutionNoteComponents/ResolutionNoteDialog';
import { durationSearchToken, durationUnits } from '../TicketTimeTracking/durationFormatter';
import { UserAvatar } from '../UserAvatar';
import { getUser } from '../../../AuthConfig';
import { commentForSaving, slateToComment } from './commentSaving';
import { groupActivitiesByDay } from './activityGrouping';
import { activityFilterProp, isAttachmentDrawnByComment } from './attachmentFilter';
import { activityCeiling, clampDraggedHeight, expandedHeights, getCommentBoxHeights, readDraggedHeight, readExpanded, resizeCursor, saveDraggedHeight, saveExpanded } from './commentBoxHeight';
import { draftPreviewText } from './draftPreview';
import { FileAlert } from './components/Alerts';
import FileAttachments from './components/FileAttachments';
import ShowEditor from './components/ShowEditor';
import TicketQuickEdit from './components/TicketQuickEdit';
import { ConversationScrollPin, observeConversationComposer } from './conversationScrollPin';
import {
  chatActivityItem,
  resolveCommentDate,
  skeletonLoader,
  useActivity
} from './TicketActivity';
import {
  ActivityItemProps,
  EditTicketProps
} from './TicketConversation.interfaces';
import AppCSS from './TicketConversation.module.css';
import TicketConversationToolbarActivity from './TicketConversationToolbarActivity';
import { keepsComposerOpen } from './composerClickAway';

const classNames = mergeStyleSets({
  userLink: {
    color: 'var(--mgt-theme-brand-foreground1)',
    ':hover, :active, :focus': {
      color: 'var(--mgt-theme-brand-foreground1) !important'
    }
  },
  TicketDetailHeader: {
    color: 'var(--mgt-theme-default-foreground2)',
  },
  MergedTicketDetail: {
    backgroundColor: 'var(--mgt-theme-brand-background1)'
  }
});
const api = new platformService();
const uploadHelper = new uploadService();
const ACTIONS = {
  TYPING: 'typing',
  CHECK: 'check',
  SENDING: 'sending',
  CALL_API: 'call-api',
  SUCCESS: 'success',
  ERROR: 'error',
  EMPTY_MESSAGE: 'empty-message',
  SET_CONVERSATION: 'set-conversation',
  SAVING_STATE: 'saving-state',
  SAVING_SUCCESS: 'saving-success',
  SET_TICKET: 'set-ticket',
  SET_SIGNEDIN_USER: 'set-signedin-user',
  SENT: 'sent',
  COMMENTING_STATE_ON: 'commenting-state-on',
  COMMENTING_STATE_OFF: 'commenting-state-off',
  SET_IS_EMPTY: 'set-is-empty',
  SET_NOT_EMPTY: 'set-not-empty'
};

const commentsReducer = (t: any) => (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_IS_EMPTY:
      return {
        ...state,
        isMessageEmpty: true
      }
    case ACTIONS.SET_NOT_EMPTY:
      return {
        ...state,
        isMessageEmpty: false
      }
    case ACTIONS.TYPING:
      return {
        ...state,
        body: action.payload.body
      };
    case ACTIONS.CHECK:
      return {
        ...state,
        isPublic: !state.isPublic
      };
    case ACTIONS.CALL_API: {
      return {
        ...state,
        loading: true
      };
    }
    case ACTIONS.SUCCESS: {
      return {
        ...state,
        loading: false
      };
    }
    case ACTIONS.SENDING: {
      return {
        ...state,
        isSending: true
      };
    }
    case ACTIONS.SENT: {
      return {
        ...state,
        isSending: false
      };
    }
    case ACTIONS.EMPTY_MESSAGE: {
      return {
        ...state,
        body: [{
          children: [{ text: '' }],
        }],
        isPublic: true
      };
    }
    case ACTIONS.SET_CONVERSATION: {
      return {
        ...state,
        conversation: action.data
      };
    }
    case ACTIONS.COMMENTING_STATE_ON: {
      return {
        ...state,
        isCommenting: true
      }
    }
    case ACTIONS.COMMENTING_STATE_OFF: {
      return {
        ...state,
        isCommenting: false
      }
    }
    default:
      throw new Error(t('ticket-details.ticket-conversation.unexpected-action'));
  }
};

const updateTicket = async (
  ticketId: number,
  data: EditTicketProps
) => {
  try {
    return await api.updateTicket(ticketId, data);
  } catch (err) {
    return err;
  }
}

const createComment = async (ticketId: number, data: { Body: string; IsPublic: boolean }) => {
  try {
    const createCommentRes = await api.addCommentToTicket(ticketId, data);
    return createCommentRes.data;
  } catch (err) {
    return err;
  }
};

interface ApproversProps {
  Id: number;
  HasApproved: number;
  Approval: string;
  ModifiedDate?: string;
}

interface Approval {
  Id: number;
  Title: string;
}

interface CommentStateProps {
  body: any;
  isPublic: boolean;
  conversation: [];
  loading: boolean;
  isSending: boolean;
  isCommenting: boolean;
  isMessageEmpty: boolean;
}

const commentInitialState: CommentStateProps = {
  body: [{
    children: [{ text: '' }],
  },
  ],
  isPublic: true,
  conversation: [],
  loading: true,
  isSending: false,
  isCommenting: false,
  isMessageEmpty: true,
};

const isSlateBodyEmpty = (data: any): boolean => {
  try {
    let text = "";
    data.forEach((d) => {
      if (d.children.length > 1) {
        throw new Error('not-empty');
      } else {
        text += d.children[0]['text'] ?? '';
        if (typeof d.type != "undefined") {
          if (d.type == 'image') {
            text += 'image';
          }

          if (d.type == 'mention') {
            text += 'mention';
          }

          if (d.type == 'bulleted-list' || d.type == 'numbered-list') {
            if (d.children[0].children[0]['text'])
              text += 'list';
          }
        }
      }
    });
    return text == "";
  } catch {
    return false;
  }
};
export const getTimeDifference = (date1: string, date2: string) => Math.abs((new Date(date1).getTime() - new Date(date2).getTime()) / 1000);
export const checkFilter = (relatedTicketIds: string, relationType: string, filterProp: string, searchText: string) =>
  filterProp === 'All' && (searchText === '' || relatedTicketIds.includes(searchText) || relationType.includes(searchText.toLowerCase()));
export const getFilteredRelatedTickets = (relatedTickets: IRelatedTickets[], activity: ActivityItemProps) => {
  switch (activity.Action) {
    case ACTIVITY_ACTION.CHANGED:
      return relatedTickets.filter(t => t.IsDeleted && t.ModifiedById === activity.ModifiedById && getTimeDifference(activity.ModifiedDate, t.ModifiedDate) <= 2.0);
    case ACTIVITY_ACTION.CREATED:
      return relatedTickets.filter(t => t.CreatedById === activity.ModifiedById && getTimeDifference(activity.ModifiedDate, t.CreatedDate) <= 2.0);
    default: return [] as IRelatedTickets[];
  }
}

export const getRelateAction = (action: string, reationType: number) => {
  if (action === ACTIVITY_ACTION.CREATED && reationType === RelationType.MergeTickets)
    return ACTIVITY_ACTION.MERGED;
  else if (action === ACTIVITY_ACTION.CREATED && reationType === RelationType.RelateTickets)
    return ACTIVITY_ACTION.RELATED;
  else if (action === ACTIVITY_ACTION.CHANGED && reationType === RelationType.RelateTickets)
    return ACTIVITY_ACTION.UNRELATED;
  else return "";
}

export const getApproverAction = (value: string) => {
  if (value != null || value != "") {
    let approverData = CommonHelper.toObject<ApproversProps>(value);
    if (approverData.HasApproved == TicketApprovalState.Approved) return "approved";
    else if (approverData.HasApproved == TicketApprovalState.Rejected) return "declined";
    else if (approverData.HasApproved == TicketApprovalState.Pending) return ACTIVITY_ACTION.RESET_VOTES_FOR;
    else return "";
  }
  else return value;
}

export const getApprovalTitle = (value: string) => {
  if (value != null || value != "") {
    let approverData = CommonHelper.toObject<ApproversProps>(value);
    let approvalData = CommonHelper.toObject<Approval>(approverData.Approval);

    if (approverData.HasApproved === TicketApprovalState.Pending)
      return value;
    else
      return approvalData?.Title ?? "";
  }
  else {
    return value;
  }
}

export const arrangeActivities = (
  activityItems: ActivityItemProps[],
  relatedTickets: IRelatedTickets[],
  filterProp: string = "All",
  searchText: string = "") => {
  const activities: ActivityItemProps[] = [];
  let relatedTicketKey = "";
  activityItems.forEach(activity => {
    if (activity.Field === ACTIVITY_FIELD.RELATE_TICKET) {
      const filteredRelatedTickets = getFilteredRelatedTickets(relatedTickets, activity);
      if (filteredRelatedTickets.length > 0) {
        const relatedTicketIds = '#' + [...filteredRelatedTickets].sort((a, b) => (a.RelatedTicketId - b.RelatedTicketId)).map(t => threedigitized(t.RelatedTicketId)).join(', #');
        const relationType = getRelateAction(activity.Action, filteredRelatedTickets[0]?.RelationshipTypeId);
        const uniqueKey = relatedTicketIds + activity.ModifiedById + activity.Action + relationType;
        if (relatedTicketKey !== uniqueKey && checkFilter(relatedTicketIds, relationType, filterProp, searchText)) {
          relatedTicketKey = uniqueKey;
          activity.Action = relationType;
          activity.RelatedTicketIds = relatedTicketIds;
          activities.push(activity);
        }
      }
    }
    else if ((activity.Field == ACTIVITY_FIELD.APPROVERS || activity.Field == ACTIVITY_FIELD.APPROVAL) && activity.Action == ACTIVITY_ACTION.CHANGED) {
      if (activity.Field == ACTIVITY_FIELD.APPROVERS) {
        activity.Action = getApproverAction(activity.Value);
        activity.Value = getApprovalTitle(activity.Value);

        let isDuplicatedEntry = false;
        const prevActivity = activities[activities.length - 1];
        if (prevActivity) {
          if (activity.Action === ACTIVITY_ACTION.RESET_VOTES_FOR && prevActivity.Action === ACTIVITY_ACTION.RESET_VOTES_FOR) {
            let approvalData = CommonHelper.toObject<ApproversProps>(activity.Value);
            let prevApprovalData = CommonHelper.toObject<ApproversProps>(activities[activities.length - 1].Value);
            const thisApprovalTitle = CommonHelper.toObject<Approval>(approvalData.Approval);
            let prevApprovalTitle = CommonHelper.toObject<Approval>(prevApprovalData.Approval);
            if (thisApprovalTitle.Title === prevApprovalTitle.Title && getTimeDifference(approvalData.ModifiedDate, prevApprovalData.ModifiedDate) <= 2.0) {
              isDuplicatedEntry = true;
            }
          }
        }
        if (!isDuplicatedEntry) {
          activities.push(activity);
        }
      }
    }
    else {
      activities.push(activity);
    }
  });
  return activities;
}

export const TicketConversation = (Props: ConversationProps) => {
  const { t } = useTranslation();
  const currentState = appState();
  const setState = useSetState();
  const chatcontainer = useRef<HTMLDivElement>(null);
  const [commentState, dispatch] = useReducer(commentsReducer(t), commentInitialState, (initial: CommentStateProps): CommentStateProps => {
    const draft = currentState.draftComment;
    if ((draft?.ticketId ?? 0) === Props.ticketId && !isSlateBodyEmpty(draft?.body ?? "")) {
      return { ...initial, body: draft.body, isMessageEmpty: false };
    }
    return initial;
  });
  const [_activityState, getTicketActivitiesById] = useActivity();
  const [pullingActivity, setPullingActivity] = useState(true);
  const [noScroll, setNoScroll] = useState(false);
  const [switchComment, setSwitchComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterProp, setFilterProp] = useState('All');
  const [searchText, setSearchText] = useState('');
  const [filterIndex, setFilterIndex] = useState<number>(0);
  const [emptySearch, setEmptySearch] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([{ key: 'All', header: t("ticket-details.ticket-conversation.all") }]);
  const [sourceTicket, setSourceTicket] = useState<IRelatedTickets>(null);
  const [mergedBy, setMergedBy] = useState<string>('');
  const [isAutoFocused, setIsAutoFocused] = useState<boolean>(false);
  const [isLifecycleEnabled, setIsLifecycleEnabled] = useState<boolean>(false);
  const [showResolvedDialog, setShowResolvedDialog] = useState(false);
  const [resolveStatusId, setResolveStatusId] = useState(null);
  const [droppedFiles, setDroppedFiles] = useState<any[]>(() =>
    currentState.draftComment?.ticketId === Props.ticketId ? (currentState.draftComment.files ?? []) : []);
  const [updatedResolution, setUpdatedResolution] = useState<ResolutionDetail>(null);
  const droppedRef = useRef<File[]>();
  droppedRef.current = droppedFiles;
  const draftCommentBodyRef = useRef(commentState.body);
  draftCommentBodyRef.current = commentState.body;
  const [droppedIndex, setDroppedIndex] = useState(() =>
    currentState.draftComment?.ticketId === Props.ticketId ? (currentState.draftComment.pastedIndex ?? 1) : 1);
  const droppedIndexRef = useRef<number>();
  droppedIndexRef.current = droppedIndex;

  const [toUpdate, setToUpdate] = useState<EditTicketProps>({
    StatusId: Props.status,
    AssigneeId: Props.assigneeId,
    DueDate: Props.dueDate,
    PriorityId: Props.priority,
  });

  const [currentTicketProps, setCurrentTicketProps] = useState<EditTicketProps>({
    StatusId: Props.status,
    AssigneeId: Props.assigneeId,
    DueDate: Props.dueDate,
    PriorityId: Props.priority,
    Resolution: Props.resolution
  });

  const [circleImages, setCircleImages] = useState<any[]>([]);
  const [circleImage, setCircleImage] = useState<any>();
  const [groupChatText, setGroupChatText] = useState('');
  // A closed ticket is read-only, its comments included: Edit and Delete are not shown on one.
  // Closing or reopening changes these, which rebuilds the feed (the effect watching them below).
  const canEditOwnComments = !Props.isTicketClosed && (Props.resolutionNoteConfig?.AgentsCanEditTheirOwnComments ?? false);
  const canDeleteOwnComments = !Props.isTicketClosed && (Props.resolutionNoteConfig?.AgentsCanDeleteTheirOwnComments ?? false);
  // Read when a feed item is BUILT, not when its fetch started. renderActivityData is recreated
  // every render, so the first fetch - started before these settings arrive - builds with them
  // false, and if it finishes after the rebuild below it overwrites it: every comment without
  // its Edit and Delete, as seen after a refresh on iPhone.
  const commentPermissions = useRef({ canEditOwnComments, canDeleteOwnComments });
  commentPermissions.current = { canEditOwnComments, canDeleteOwnComments };

  const updateFile = (index: number, progress: number) => {
    droppedFiles[index].isInProgress = (progress < 100);
    droppedFiles[index].progressPercent = progress;
    droppedFiles[index].isUploading = (progress < 100);

    setDroppedFiles((items) => {
      return items.map((item, idx) => {
        if (idx === index) {
          item.isInProgress = (progress < 100);
          item.progressPercent = progress;
          item.isUploading = (progress < 100);
        }
        return item;
      });
    });
  }

  const uploadFilesComment = async (message: string, files: File[], isPrivate: boolean) => {
    const jobs = [];
    const queue = AsyncQueue(2);

    files.forEach((file, idx) => jobs.push(queue.add(async () => {
      // Get the Upload URL
      const upload = await api.getUploadUrl(Props.ticketId, file.name, file.size);

      // Upload the File
      const url = upload.data;
      const response = await uploadHelper.uploadFile(url.uploadUrl, file, (u, t) => updateFile(idx, (u * 1.0 / t) * 100));

      // Set File Complete
      updateFile(idx, 100);
      response.data.groupId = url.groupId;
      response.data.driveId = url.driveId;

      return response;
    })));

    const results = await Promise.allSettled(jobs);

    const filesToAttach = results
      .map(file => (file.status == "fulfilled" && file.value.data.id) ? { FileName: file.value.data.name, FileSize: file.value.data.size, DriveItemId: file.value.data.id, ...attachmentLocation(file.value.data), DownladUrl: file.value.data.webUrl } : null)
      .filter(x => x !== null);
    const uploadedFiles: FileProps[] = await api.attachTicketFiles(Props.ticketId, filesToAttach, message, isPrivate, Props.isEndUserPage, false);
    return uploadedFiles;
  }

  useEffect(() => {
    let status = getStatusIdByGuid(currentState.ticketStatus, STATUS_GUID.RESOLVED);
    setResolveStatusId(status);

  }, []);

  // The scroll pin is re-attached whenever the pane or the composer is a different element.
  // Attached once on mount, it was left watching a pane React had since replaced - the
  // loader and empty-search items above it shift its position while the ticket loads -
  // so on mobile it never reserved any room for the composer at all.
  const scrollPin = useRef<{ chat: HTMLElement | null; composer: HTMLElement | null; pin: ConversationScrollPin } | null>(null);
  useEffect(() => {
    const chat = chatcontainer.current;
    const composer = chat?.parentElement?.querySelector<HTMLElement>('.comment-composer') ?? null;
    if (scrollPin.current?.chat === chat && scrollPin.current?.composer === composer) return;
    // A replaced pane keeps the reader's choice: following the bottom, or scrolled up reading.
    const following = scrollPin.current?.pin.isFollowing() ?? true;
    scrollPin.current?.pin.stop();
    scrollPin.current = { chat, composer, pin: observeConversationComposer(chat, composer, following) };
  });
  useEffect(() => () => scrollPin.current?.pin.stop(), []);

  // TicketConversation unmounts whenever the Lifecycle/Form/Resolution tab is
  // selected (TicketTabsContent only renders the 'activity' tab's tree), which
  // otherwise loses whatever the agent had typed or attached but not yet sent. Stash
  // it in appState on unmount, keyed by ticket, and the lazy inits above restore it
  // the next time this ticket's activity tab remounts. While it is stashed the
  // property form still counts it, so leaving the ticket asks first; switching tabs
  // does not, as that is no route change.
  const pastedIndexRef = useRef(droppedIndex);
  pastedIndexRef.current = droppedIndex;
  // Any route change is one the leave prompt let through - it blocks the rest - so the agent
  // chose to leave, or there was nothing to warn about. That includes clicking the open
  // ticket again, which reloads it: the draft is discarded then too, as on leaving.
  const history = useHistory();
  const location = useLocation();
  const openedAt = useRef(location.key);
  useEffect(() => {
    if (location.key === openedAt.current) return;
    openedAt.current = location.key;
    setDroppedFiles([]);
    dispatch({ type: ACTIONS.EMPTY_MESSAGE });
    dispatch({ type: ACTIONS.SET_IS_EMPTY });
    dispatch({ type: ACTIONS.COMMENTING_STATE_OFF });
  }, [location.key]);
  useEffect(() => {
    // This ticket's stash is taken back into the box, and from here the box's own state
    // reports it - a stale copy would keep the leave warning up after the comment was sent.
    // Another ticket's stash is one the agent was warned about and chose to leave.
    setState(s => (s.draftComment ? { ...s, draftComment: null } : s));
    return () => {
      const body = draftCommentBodyRef.current;
      const files = droppedRef.current ?? [];
      // Unmounted by a route change, not a tab switch: discarded, as above.
      const left = history.location.key !== openedAt.current;
      setState(s => ({
        ...s,
        draftComment: left || (isSlateBodyEmpty(body) && files.length === 0)
          ? null
          : { ticketId: Props.ticketId, body, files, pastedIndex: pastedIndexRef.current }
      }));
    };
  }, []);

  useEffect(() => {
    //separate setting of resolution note because Props.isResolutionNoteEnabled and Props.resolutionNoteConfig is not yet set when setting the setProperties.
    if (Props.isResolutionNoteEnabled && Props.resolutionNoteConfig.IsResolutionNoteEnabled && Props.ticketData?.ResolutionId != null) {
      let _properties = properties;
      _properties.push({ key: 'Note', header: t('resolution-note.resolution-note') });
      _properties.push({ key: 'InternalNote', header: t('resolution-note.resolution-note-internal') });
      _properties.push({ key: 'ResolutionCategory', header: t('resolution-note.resolution-category') });
      _properties.push({ key: 'DraftKB', header: t('resolution-note.draft-kb') });
      setProperties(_properties);
    }
  }, [properties]);

  const sendComment = async (isPrivate?: boolean) => {
    if (resolutionNoteValid()) {
      Props.ticketData.StatusId = Props.status;
      setShowResolvedDialog(true);
    }
    else
      await sendCommentNow(false, isPrivate);

  };

  const resolutionNoteValid = () => Props.isResolutionNoteEnabled && Props.resolutionNoteConfig.IsResolutionNoteEnabled && Props.status != resolveStatusId && toUpdate.StatusId == resolveStatusId;

  const sendCommentNow = async (withResolutionNote: boolean, isPrivate?: boolean) => {

    dispatch({ type: ACTIONS.SENDING });

    if (resolutionNoteValid() && withResolutionNote) {
      toUpdate.Resolution.AnswerId = null;
      if (toUpdate?.Resolution?.DraftKB) {
        let kbId = await AddOrUpdateResolutionToDraftKB(toUpdate.Resolution, Props.formState.defaultTicket, currentState, t);
        toUpdate.Resolution.AnswerId = kbId;

        Props.dispatch({ type: Props.actions.UPDATE_TICKET, data: { AnswerId: kbId } });
      }

      if (Props.formState.updatedTicket.hasOwnProperty('ResolutionCategoryId')) {
        Props.formState.updatedTicket["ResolutionCategory"] = clone(toUpdate.Resolution.ResolutionCategory);
        toUpdate.Resolution.ResolutionCategoryId = toUpdate.Resolution.ResolutionCategory.Id;
      }

    }

    const mdfied = commentForSaving(slateToComment(commentState.body));
    const currentdate = new Date();
    if (droppedFiles.length > 0) {
      uploadFilesComment(mdfied, droppedFiles, isPrivate).then((data) => {
        if (Props.setUploadedFiles)
          Props.setUploadedFiles(data);

        setState(s => ({
          ...s,
          uploadingState: {
            ...s.uploadingState,
            uploadedFiles: [...data, ...s.uploadingState.uploadedFiles]
          }
        }));
      }).then(() => {
        setDroppedFiles([]);
        updateTicketByComment(currentdate, commentState.conversation.length)
      }).then(() => {
        Props.setReloadConversation(true);
        dispatch({ type: ACTIONS.EMPTY_MESSAGE });
        dispatch({ type: ACTIONS.SET_IS_EMPTY });
        dispatch({ type: ACTIONS.COMMENTING_STATE_OFF });
        dispatch({ type: ACTIONS.SENT });
      })
    } else if (!commentState.isMessageEmpty) {
      createComment(Props.ticketId, { Body: mdfied, IsPublic: !isPrivate }).then(res => {
        dispatch({ type: ACTIONS.EMPTY_MESSAGE });
        let currentMessage = {
          contentPosition: 'end',
          gutter: (
            <UserAvatar
              {...{
                idOrUpn: Props.signedInUser.Email,
                avatarProps: { name: Props.signedInUser.FullName, size: 'smaller' }
              }}
            />
          ),
          message: {
            // This bubble is a stand-in, shown straight away and replaced about a second
            // later by the real one that chatActivityItem renders from the refetched
            // activity. Anything that differs between the two is a visible jump when the
            // swap happens, so the class and the (absent) style below are kept in step with
            // the 'end' case there rather than chosen for this bubble.
            //
            // One difference is left on purpose: the real bubble carries the Edit and Delete
            // buttons, which are visibility:hidden until hover but still occupy 42px of its
            // header. So the swap grows 4px taller, and on a narrow screen - where that header
            // is the widest thing in the bubble - about 5px wider. Closing that means building
            // this stand-in through chatActivityItem rather than by hand; measured and left as
            // it is, so the next person does not go hunting for a bug here.
            className: 'w-full md:w-11/12 min-w-0 max-w-full flex flex-row-reverse',
            content: (<Flex className="w-full items-center">
              <Flex column className={`w-full`}>
                <Flex className='items-center justify-end'>
                  <Chat.Message
                    content={<Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>{mdfied}</Markdown>}
                    author={Props.signedInUser.FullName}
                    header={
                      <Text
                        size='small'
                        timestamp
                        color='grey'
                        content={
                          <div className='flex items-center'>
                            <span className='leading-5 pr-2'>
                              {resolveCommentDate(currentdate.toString())}
                            </span>
                            {isPrivate &&
                              <LockSolidIcon className='text-private' />
                            }
                          </div>
                        }
                      />
                    }
                    mine
                  />
                </Flex>
              </Flex>
            </Flex>)
          },
          key: `message-id-${res.Id}`
        };
        return { message: currentMessage, id: res.Id };
      }).then((currentMessage) => {
        appendActivityAndConvo(currentMessage, currentdate);
      }).then(() => {
        Props.setReloadConversation(true)
        dispatch({ type: ACTIONS.EMPTY_MESSAGE });
        dispatch({ type: ACTIONS.SET_IS_EMPTY });
        dispatch({ type: ACTIONS.SENT });
        dispatch({ type: ACTIONS.COMMENTING_STATE_OFF });
      });
    }
  };

  const sendInternalNote = useCallback(() => sendComment(true), [sendComment]);
  const sendCommentCallback = useCallback(() => { sendComment(); }, [sendComment]);

  const appendActivityAndConvo = (
    currentMessage: {
      message: {
        contentPosition?: string;
        gutter?: JSX.Element;
        message: {
          className: string;
          content: JSX.Element;
        }
        key?: string;
      };
      id: any;
    },
    commentTime: Date
  ) => {
    if (toUpdate.AssigneeId === currentTicketProps.AssigneeId && toUpdate.DueDate == currentTicketProps.DueDate && toUpdate.PriorityId === currentTicketProps.PriorityId && toUpdate.StatusId === currentTicketProps.StatusId) {
      dispatch({ type: ACTIONS.SET_CONVERSATION, data: [...commentState.conversation, currentMessage.message] });
      dispatch({ type: ACTIONS.SENT });
      dispatch({ type: ACTIONS.COMMENTING_STATE_OFF });
      if (Props.onSendComment) Props.onSendComment(commentTime);
      if (Props.resetCommentsCount) Props.resetCommentsCount();
      chatcontainer.current.scrollTop = chatcontainer?.current?.scrollHeight;
    }
    else {
      updateTicketByComment(commentTime, parseInt(currentMessage.id) + 1, currentMessage.message)
    }
  }

  const updateTicketByComment = async (commentTime: Date, currentId: number, message?: any) => {
    let updatesActivity = [];

    updateTicket(Props.ticketId, toUpdate).then(async (res: any) => {
      if (res.status === 204) {
        if (Props.onTicketUpdate) Props.onTicketUpdate(toUpdate, new Date(Date.now()));
        const data = JSON.parse(res.config.data);
        if (toUpdate.DueDate != currentTicketProps.DueDate) {
          let activity = {
            Action: "changed",
            Field: "DueDate",
            Id: currentId++,
            ModifiedById: Props.signedInUser.Id,
            ModifiedDate: (new Date()).toLocaleDateString(),
            ModifierAvatar: null,
            ModifierInfo: `{\"Name\":\"${Props.signedInUser.FullName}\",\"Id\":${Props.signedInUser.Id},\"Email\":\"${Props.signedInUser.Email}\"}`,
            TicketId: Props.ticketId,
            // A cleared due date has no date to show - new Date(null) would read 1/1/1970.
            Value: data.DueDate ? (new Date(data.DueDate)).toLocaleDateString() : '',
          }
          updatesActivity.push(chatActivityItem(activity, "end", Props.signedInUser, Props.isEndUserPage, {
            teamsConfig: currentState.teamsConfig,
            globalTheme: Props.globalTheme,
            canEditOwnComments: commentPermissions.current.canEditOwnComments,
            canDeleteOwnComments: commentPermissions.current.canDeleteOwnComments,
          }));
        }
        if (toUpdate.AssigneeId !== currentTicketProps.AssigneeId) {
          let newAssignee = await getCachedUser(data.AssigneeId);

          let activity = {
            Action: "assigned",
            Field: "Assignee",
            Id: currentId++,
            ModifiedById: Props.signedInUser.Id,
            ModifiedDate: (new Date()).toISOString(),
            ModifierAvatar: null,
            ModifierInfo: `{\"Name\":\"${Props.signedInUser.FullName}\",\"Id\":${Props.signedInUser.Id},\"Email\":\"${Props.signedInUser.Email}\"}`,
            TicketId: Props.ticketId,
            Value: JSON.stringify({
              Name: newAssignee?.FullName ?? 'Unassigned',
              Id: newAssignee?.Id ?? 0,
              Email: newAssignee?.Email
            }),
          };
          updatesActivity.push(chatActivityItem(activity, "end", Props.signedInUser, Props.isEndUserPage, {
            teamsConfig: currentState.teamsConfig,
            globalTheme: Props.globalTheme,
            canEditOwnComments: commentPermissions.current.canEditOwnComments,
            canDeleteOwnComments: commentPermissions.current.canDeleteOwnComments,
          }));
        }
        if (toUpdate.PriorityId !== currentTicketProps.PriorityId) {
          let activity = {
            Action: "changed",
            Field: "Priority",
            Id: currentId++,
            ModifiedById: Props.signedInUser.Id,
            ModifiedDate: (new Date()).toISOString(),
            ModifierAvatar: null,
            ModifierInfo: `{\"Name\":\"${Props.signedInUser.FullName}\",\"Id\":${Props.signedInUser.Id},\"Email\":\"${Props.signedInUser.Email}\"}`,
            TicketId: Props.ticketId,
            Value: currentState.priority.find(priority => priority.Id === data.PriorityId).Value,
          }
          updatesActivity.push(chatActivityItem(activity, "end", Props.signedInUser, Props.isEndUserPage, {
            teamsConfig: currentState.teamsConfig,
            globalTheme: Props.globalTheme,
            canEditOwnComments: commentPermissions.current.canEditOwnComments,
            canDeleteOwnComments: commentPermissions.current.canDeleteOwnComments,
          }));
        }
        if (toUpdate.StatusId !== currentTicketProps.StatusId) {
          let activity = {
            Action: "changed",
            Field: "Status",
            Id: currentId++,
            ModifiedById: Props.signedInUser.Id,
            ModifiedDate: (new Date()).toISOString(),
            ModifierAvatar: null,
            ModifierInfo: `{\"Name\":\"${Props.signedInUser.FullName}\",\"Id\":${Props.signedInUser.Id},\"Email\":\"${Props.signedInUser.Email}\"}`,
            TicketId: Props.ticketId,
            Value: currentState.ticketStatus.find(status => status.Id === data.StatusId).Value,
          }
          updatesActivity.push(chatActivityItem(activity, "end", Props.signedInUser, Props.isEndUserPage, {
            teamsConfig: currentState.teamsConfig,
            globalTheme: Props.globalTheme,
            canEditOwnComments: commentPermissions.current.canEditOwnComments,
            canDeleteOwnComments: commentPermissions.current.canDeleteOwnComments,
          }));
        }
        if (message) {
          dispatch({ type: ACTIONS.SET_CONVERSATION, data: [...commentState.conversation, message, ...updatesActivity] });
          dispatch({ type: ACTIONS.SENT });
        }
      } else {
        if (Props.onSendComment) Props.onSendComment(commentTime);
      }
      if (message) {
        dispatch({ type: ACTIONS.COMMENTING_STATE_OFF });
      }
      if (Props.resetCommentsCount) Props.resetCommentsCount();
      chatcontainer.current.scrollTop = chatcontainer?.current?.scrollHeight;

      setCurrentTicketProps(toUpdate)
    });
  }

  const onScrollLoadMoreActivity = () => {
    if (!pullingActivity || loading) {
      if (chatcontainer.current.scrollTop <= chatcontainer.current.scrollHeight * 0.01 && !noScroll) {
        chatcontainer.current.scrollTop = chatcontainer.current.scrollTop + 50;
      }
      return;
    }
    if (chatcontainer.current.scrollTop <= chatcontainer.current.scrollHeight * 0.15) {
      setPullingActivity(false);
      renderActivityData(filterCompose(), true);
    }
  };

  function onChangeHideHistory(_ev?: React.SyntheticEvent<HTMLElement>) {
    setSwitchComment(!switchComment);

    localStorage.setItem('only_comments', (!switchComment).toString());
    setLoading(true);
    setNoScroll(false);

    dispatch({ type: ACTIONS.SET_CONVERSATION, data: [] });
    dispatch({ type: ACTIONS.SUCCESS });
  }

  const onChangeHideHistoryCallbackWrapper = useCallback((_ev: React.SyntheticEvent<HTMLElement>) => {
    onChangeHideHistory(_ev);
  }, [onChangeHideHistory]);

  const renderActivityData = debounce(async (filter: string = '', isTrigger: boolean = false, reloadAll: boolean = false) => {
    const scrollEl = chatcontainer.current;
    const wasNearBottom = scrollEl
      ? scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < 100
      : true;
    const isRelatedTicketEnabled = await getCachedFeature(LICENSE_FEATURE.RelatedTickets);
    const getOnlyComments = localStorage.getItem('only_comments') || 'false';
    let onlyComments = getOnlyComments.toLowerCase() === 'true';
    const results = await Promise.all([
      getTicketActivitiesById(Props.ticketId, onlyComments, filter, isTrigger, reloadAll),
      isRelatedTicketEnabled ? api.getRelatedTicketsIncludeDeleted(Props.ticketId) : Promise.resolve([])
    ]);

    let messages = [];
    let { activities, endOfList } = results[0];
    const relatedTickets: IRelatedTickets[] = results[1];

    setEmptySearch(false);

    const filteredActivitites = filterActivities(activities);
    const allActivitites = arrangeActivities(filteredActivitites, relatedTickets, filterProp, searchText);
    const groupActivities = groupActivitiesByDay(allActivitites);

    setState(s => ({ ...s, activities: filteredActivitites }));

    groupActivities.forEach(({ label, items: activityItems }) => {
      // First, collect valid activities that will actually be displayed
      const activitiesToDisplay: Array<{ activity: ActivityItemProps; position: string }> = [];

      activityItems.forEach(activity => {
        const activityValue = getActivityValue(activity);
        if (activityValue) {
          let position = Props.signedInUser.Id == activity.ModifiedById && activityValue.CreatedById != null ? 'end' : 'start';
          if (!isAttachmentDrawnByComment(activity, filterProp)) {
            activitiesToDisplay.push({ activity, position });
          }
        }
      });

      // Only add date separator if there are activities to display in this group
      if (activitiesToDisplay.length > 0 && label !== '') {
        messages.push(getDateSeprator(activityItems[0], label));
      }

      // Add the activities to messages
      activitiesToDisplay.forEach(({ activity, position }) => {
        messages.push(chatActivityItem(activity, position, Props.signedInUser, Props.isEndUserPage, {
          teamsConfig: currentState.teamsConfig,
          globalTheme: Props.globalTheme,
          canEditOwnComments: commentPermissions.current.canEditOwnComments,
          canDeleteOwnComments: commentPermissions.current.canDeleteOwnComments,
          onDeleteSuccess: () => {
          // Refresh activities after comment deletion. reloadAll keeps every
          // loaded page so the view stays put instead of jumping to the latest.
          const filter = (!switchComment) ? filterCompose() : encodeURI("Property eq All,NewValue eq ");
          renderActivityData(filter, false, true);
        },
          onCommentUpdated: Props.onCommentUpdated,
        }));
      });
    });

    if (allActivitites.length === 0) setEmptySearch(true);

    if (endOfList) {
      setNoScroll(true);
      setPullingActivity(false);
    }
    else {
      setPullingActivity(true);
      if (onlyComments) {
        messages.unshift(skeletonLoader('600px').comment);
        messages.unshift(skeletonLoader('400px').comment);
      } else {
        messages.unshift(skeletonLoader().comment);
        messages.unshift(skeletonLoader().activity);
      }
    }
    dispatch({ type: ACTIONS.SET_CONVERSATION, data: messages });
    dispatch({ type: ACTIONS.SUCCESS });

    if (!isTrigger && wasNearBottom && chatcontainer.current) chatcontainer.current.scrollTop = chatcontainer.current.scrollHeight;

    setLoading(false);

  }, 500);

  const getActivityValue = (activity: ActivityItemProps) => {
    let activityValue: any;
    const fieldsWithNoCreatedById = [
      ACTIVITY_FIELD.RELATE_TICKET,
      ACTIVITY_FIELD.CARD_ANSWER,
      ACTIVITY_FIELD.ASSIGNEE,
      ACTIVITY_FIELD.REQUESTER,
      ACTIVITY_FIELD.RESOLUTATION_DATE,
      ACTIVITY_FIELD.DUE_DATE,
      ACTIVITY_FIELD.FILE_ATTACHMENT,
      ACTIVITY_FIELD.TICKET_COLLABORATORS,
      ACTIVITY_FIELD.AFFECTED_USERS,
      ACTIVITY_FIELD.TICKET_TASKS,
      ACTIVITY_FIELD.TICKET_APPROVAL,
      ACTIVITY_FIELD.TICKET_RESOLUTION_CATEGORY,
      ACTIVITY_FIELD.GROUP_CHAT,
      ACTIVITY_FIELD.TICKET_CUSTOM_FORM,
      ACTIVITY_FIELD.TICKET_CUSTOM_FORM_ANSWER
    ];
    try {
      activityValue = JSON.parse(activity.Value || '{}');
      if (typeof activityValue !== 'object')
        return { CreatedById: 0 };

      if (fieldsWithNoCreatedById.includes(activity.Field)) activityValue.CreatedById = 0;
    } catch {
      activityValue = { CreatedById: 0 };
    }
    return activityValue;
  }

  const getDateSeprator = (activityItem: ActivityItemProps, label: string) => {
    const style = { backgroundColor: 'var(--mgt-theme-default-foreground2)', opacity: 0.1, height: 1 };
    const sepratorOptions = {
      root: { zIndex: 1, ...{ '::before': style }, ...{ '::after': style } },
      content: { backgroundColor: 'var(--mgt-theme-background2)', padding: '5px 5px 5px 0', fontWeight: '600' }
    };

    return {
      children: (
        <Stack style={{ width: '100%', zIndex: 1 }}>
          <Separator alignContent="center" styles={{ ...sepratorOptions }}>
            <span style={{ color: 'var(--mgt-theme-default-foreground)', padding: '0 15px', opacity: 0.8, fontSize: 12 }}>
              {label}
            </span>
          </Separator>
        </Stack>
      ),
      key: `group-id-${activityItem.Id}`
    }
  }

  const filterActivities = (activityItems: ActivityItemProps[]) => {
    if (!activityItems) return [];
    const hideActivityFromFields = [ACTIVITY_FIELD.CARD_TEMPLATE, ACTIVITY_FIELD.IS_MERGED];
    const endUserRequestActivityFromFields = [
      ACTIVITY_FIELD.INIT,
      ACTIVITY_FIELD.ASSIGNEE,
      ACTIVITY_FIELD.STATUS,
      ACTIVITY_FIELD.FILE_ATTACHMENT,
      ACTIVITY_FIELD.CARD_ANSWER,
      ACTIVITY_FIELD.RESOLUTATION_DATE,
      ACTIVITY_FIELD.COMMENTS,
      ACTIVITY_FIELD.TICKET_COLLABORATORS,
      ACTIVITY_FIELD.AFFECTED_USERS,
      ACTIVITY_FIELD.TICKET_RESOLUTION_NOTE,
      ACTIVITY_FIELD.GROUP_CHAT
    ];

    return activityItems.filter(activity => ((!Props.isEndUserPage && !hideActivityFromFields.includes(activity.Field)) || (Props.isEndUserPage && endUserRequestActivityFromFields.includes(activity.Field))) &&
      (activity.Field != ACTIVITY_FIELD.COMMENTS || !Props.isEndUserPage || (activity.Field === ACTIVITY_FIELD.COMMENTS && Props.isEndUserPage && JSON.parse(activity.Value || '{}')?.IsPublic)));
  }
  const commentsDisabled = () => commentState.isSending || (commentState.isMessageEmpty && droppedFiles.length === 0) || Props.isTicketClosed;
  /**
   * With the Time Entry filter on, a typed duration is translated into the token the
   * activity row stores before it is sent.
   *
   * The feed renders durations through each viewer's own locale, so the server cannot match
   * on what was typed. Parsing here - where the locale and the patterns already are - keeps
   * the wire value numeric and language-free.
   *
   * It also removes the noise: the stored payload carries a timestamp, so a raw search for
   * "4" used to match nearly every row. Translated, "4" means four minutes.
   *
   * Anything that is not a duration falls through untouched, so searching an agent's name
   * still works - the server resolves that against the user table.
   */
  const searchValue = () => {
    if (filterProp !== 'TimeEntries') return searchText;
    const { hoursUnit, minutesUnit } = durationUnits(t);
    return durationSearchToken(searchText, hoursUnit, minutesUnit) ?? searchText;
  };

  // The value must not contain a comma: GetActivities splits this string on commas and
  // takes the second field, so one would truncate the search silently.
  const filterCompose = () => encodeURI(`Property eq ${filterProp.removeWhiteSpaces()},NewValue eq ${searchValue()}`);

  const searchActivityHandler = () => {
    setEmptySearch(false);
    renderActivityData(filterCompose());
    setLoading(true);

    dispatch({ type: ACTIONS.SET_CONVERSATION, data: [] });
    dispatch({ type: ACTIONS.SUCCESS });
  };

  useEffect(() => {
    setLoading(true);
    dispatch({ type: ACTIONS.CALL_API });
    dispatch({ type: ACTIONS.SET_CONVERSATION, data: [] });
    // A ticket opens at the bottom of its activity, however many passes its load takes.
    scrollPin.current?.pin.stick();
    if (Props.ticketId && Props.signedInUser) renderActivityData();
  }, [Props.ticketId, Props.signedInUser.Id]);

  useEffect(() => {
    const filter = (!switchComment) ? filterCompose() : encodeURI("Property eq All,NewValue eq ");
    setTimeout(() => renderActivityData(filter), 300);
  }, [switchComment]);

  useEffect(() => {
    const onlyComments = localStorage.getItem('only_comments') || 'false';
    setSwitchComment(onlyComments === 'true');
  }, [setSwitchComment])

  useEffect(() => {
    if (Props.reloadConversation) {
      // Covers edits and new comments; reloadAll preserves the scroll position
      // when editing a comment further back in the history.
      const filter = (!switchComment) ? filterCompose() : encodeURI("Property eq All,NewValue eq ");
      setTimeout(() => renderActivityData(filter, false, true), 300);
      Props.setReloadConversation(() => { return false; });
    }
  }, [Props.reloadConversation]);

  useEffect(() => {
    // Permissions can arrive after initial activity load; rebuild message items
    // so Edit/Delete button visibility reflects the latest flags.
    if (Props.ticketId && Props.signedInUser) {
      const filter = (!switchComment) ? filterCompose() : encodeURI("Property eq All,NewValue eq ");
      setTimeout(() => renderActivityData(filter, false, true), 300);
    }
  }, [canEditOwnComments, canDeleteOwnComments]);

  useEffect(() => {
    setDroppedIndex(Props.droppedIndex)
  }, []);

  useEffect(() => {
    setTimeout(() => searchActivityHandler(), 300);
  }, [filterProp, searchText]);

  useEffect(() => {
    (async () => {
      const featureResult = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.TicketTypes),
        getCachedFeature(LICENSE_FEATURE.MultiDepartment),
        getCachedFeature(LICENSE_FEATURE.TicketLifecycle),
        getCachedFeature(LICENSE_FEATURE.TicketTasks),
        getCachedFeature(LICENSE_FEATURE.Approvals),
        getCachedFeature(LICENSE_FEATURE.TicketTags),
        getCachedFeature(LICENSE_FEATURE.AffectedUsers),
        getCachedFeature(LICENSE_FEATURE.Collaborators),
        getCachedFeature(LICENSE_FEATURE.TimeTracking)
      ]);
      const enabled = featureResult[0];
      const enabledMultiDepartment = featureResult[1];
      const enabledTicketLifecycle = featureResult[2];
      setIsLifecycleEnabled(enabledTicketLifecycle);
      const enabledTicketTasks = featureResult[3];
      const enabledApprovals = featureResult[4];
      const enabledTicketTags = featureResult[5];
      const enabledUsers = featureResult[6];
      const enabledCollabs = featureResult[7];
      // Already combines the licence and the tenant setting - see GetAvailableFeatures - so
      // one flag answers both questions.
      const enabledTimeTracking = featureResult[8];
      let searchers = [
        { key: 'Status', header: t('ticket-details.edit-ticket-view.filter.status') },
        { key: 'Ticket Type', header: t('ticket-details.edit-ticket-view.filter.type') },
        { key: 'Category', header: t('ticket-details.edit-ticket-view.filter.category') },
        { key: 'Priority', header: t('ticket-details.edit-ticket-view.filter.priority') },
        { key: 'Requester', header: t('ticket-details.edit-ticket-view.filter.requester') },
        { key: 'Assignee', header: t('ticket-details.edit-ticket-view.filter.assignee') },
        { key: 'DueDate', header: t('ticket-details.edit-ticket-view.filter.due-date') },
        { key: 'Team', header: t('ticket-details.edit-ticket-view.filter.team') },
        { key: 'Group', header: t('ticket-details.edit-ticket-view.filter.group') },
        { key: 'Tags', header: t('ticket-details.edit-ticket-view.filter.tags') },
        { key: 'Attachments', header: t('ticket-details.edit-ticket-view.filter.attachments') },
        { key: 'Tasks', header: t('ticket-details.edit-ticket-view.filter.tasks') },
        { key: 'Approval', header: t('ticket-details.edit-ticket-view.filter.approval') },
        { key: 'Lifecycle', header: t('ticket-details.edit-ticket-view.filter.lifecycle') },
        { key: 'AffectedUsers', header: t('ticket-details.edit-ticket-view.filter.affected-users') },
        { key: 'TicketCollaborators', header: t('ticket-details.edit-ticket-view.filter.collaborators') }
      ];
      let excludeCols = [];
      if (!enabled) excludeCols.push("Ticket Type");
      if (!enabledMultiDepartment) excludeCols.push("Team");
      if (!enabledTicketLifecycle) excludeCols.push("Lifecycle");
      if (!enabledTicketTasks) excludeCols.push("Tasks");
      if (!enabledApprovals) excludeCols.push("Approval");
      if (!enabledTicketTags) excludeCols.push("Tags");
      if (!enabledUsers) excludeCols.push("AffectedUsers");
      if (!enabledCollabs) excludeCols.push("TicketCollaborators");

      if (excludeCols.length > 0)
        searchers = searchers.filter(x => !excludeCols.includes(x.header));

      // Pushed conditionally rather than added above and excluded through excludeCols. That
      // path compares against x.header, the *translated* label, while excludeCols holds
      // English keys - so it silently fails in every non-English locale, and for three
      // entries in English too ("Tags" vs "Tag(s)"). Adding it here keeps the gate working.
      //
      // Key must stay TimeEntries: it is the Property on the stored activity rows, and
      // GetActivities matches it with Contains, so nothing needs registering server-side.
      if (enabledTimeTracking)
        searchers.push({ key: 'TimeEntries', header: t('ticket-details.edit-ticket-view.filter.time-entry') });

      searchers.sort((a, b) => a.header.localeCompare(b.header));
      searchers.splice(0, 0, { key: 'All', header: t('ticket-details.edit-ticket-view.filter.all') });

      setProperties(searchers);
    })();
  }, []);

  const [isShowFileAlert, setIsShownFileAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDropAccepted: (acceptedFiles: File[]) => {
      if (!commentState.isSending) {
        let filteredFiles: File[] = []
        if ((acceptedFiles.length + droppedFiles.length) <= 10) {
          acceptedFiles.forEach((acceptedFile: File) => {
            if (droppedFiles.some(f => f.name == acceptedFile.name && f.size == acceptedFile.size)) {
              setIsShownFileAlert(true)
              setAlertMessage(t("ticket-details.ticket-conversation.file-already-attached-error"))
              setTimeout(() => {
                setIsShownFileAlert(false)
                setAlertMessage('')
              }, 3000)
            } else {
              filteredFiles.push(acceptedFile)
            }
          });

          setDroppedFiles((prev) => ([
            ...prev, ...filteredFiles
          ]));
        } else {
          setIsShownFileAlert(true)
          setAlertMessage(t("ticket-details.ticket-conversation.number-of-files-on-upload-error"))
          setTimeout(() => {
            setIsShownFileAlert(false)
            setAlertMessage('')
          }, 3000)
        }
        dispatch({ type: ACTIONS.COMMENTING_STATE_ON });
      }
    },
    disabled: Props.isDropzoneDisabled
  });

  useEffect(() => {
    if (Props.isTicketMerged) setMergedSourceTicket();
  }, [Props.ticketId, Props.isTicketMerged]);

  useEffect(() => {
    if (Props.isTicketClosed) dispatch({ type: ACTIONS.COMMENTING_STATE_OFF });
  }, [Props.isTicketClosed]);

  useEffect(() => {
    showOpenGroupChatWith();
  }, [Props.groupChat]);

  const showOpenGroupChatWith = () => {
    if (!Props.groupChat?.Members) return;

    const promises = Props.groupChat.Members.map(user => {
      const id = user?.AadObjectId ?? user.UserName;
      return getCachedPhoto(id).then(imageUrl =>
        imageUrl && imageUrl.length > 0 ? imageUrl : null
      );
    });

    Promise.all(promises).then(images => {
      const uniqueImages = [...new Set(images)].filter(Boolean).map(String);
      setCircleImages(uniqueImages);
      const groupChatTitle = t('group-chat.open-header');
      let memberString = Props.groupChat.Members.map(m => m.FullName).join(', ');
      if (Props.groupChat.Members.length > 3) {
        memberString = `${Props.groupChat.Members.slice(0, 3).map(m => m.FullName).join(', ')} and ${Props.groupChat.Members.length - 3} other(s)`;
      }
      setGroupChatText(groupChatTitle + ' ' + memberString);
    });
    Props.setReloadConversation(true);
  }

  useEffect(() => {
    (async () => {
      if (circleImages.length > 0) {
        const circleImage = await generateCircleImage(circleImages, 3);
        const imgElement = (
          <img
            src={circleImage}
            alt="Group Chat Members"
            style={{ borderRadius: '50%', width: '24px', height: '24px' }}
          />
        );
        setCircleImage(imgElement);
      }
    })();
  }, [circleImages]);

  const setMergedSourceTicket = async () => {
    setMergedBy('');
    setMergedByUser(null);
    const ticket: IRelatedTickets = await api.getMergedSourceTicket(Props.ticketId);
    if (ticket) {
      setSourceTicket(ticket);
      setMergedByUser(ticket.CreatedById);
    }
  }

  const setMergedByUser = async (createdById: number) => {
    const user = await getCachedUser(createdById);
    setMergedBy(user?.FullName ?? '');
  }

  const searchTimeout = useRef(null);
  const onSearch = (e: any, v) => {
    if (e.currentTarget.value) setIsAutoFocused(true);
    setSearchQuery(v.value);
    //Used setTimout instead of debounce becuase if you keep on typing, debounce will be triggered when the delay you set is met.
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearchText(v.value);
    }, 500);
  };

  const handleSearchClick = (_e: any) => setSearchText(searchQuery);

  const handleSearchClickCallbackWrapper = useCallback((_e: any) => {
    handleSearchClick(_e);
  }, [handleSearchClick]);

  const onChangefilterIndex = (_event, props) => {
    let active = typeof props.highlightedIndex == 'undefined' ? 0 : props.highlightedIndex;
    setFilterIndex(active);
    setFilterProp(activityFilterProp((props.value).key));
  }

  const onChangefilterIndexCallbackWrapper = useCallback((_event, props) => {
    onChangefilterIndex(_event, props);
  }, [onChangefilterIndex]);

  const renderMergedTicketDetails = () => {
    const redirectPage = Props.isEndUserPage ? "requests" : "tickets";
    return <>
      <CheckFeature featureName={LICENSE_FEATURE.RelatedTickets}>
        <div className={`my-2 py-4 px-5 rounded-md w-full h-85 ${classNames.MergedTicketDetail}`}>
          {sourceTicket && mergedBy ? <>
            <div className={`font-bold ${classNames.TicketDetailHeader}`}>{t("ticket-details.ticket-conversation.merge-with-ticket")}&nbsp;
              <Link className={`${classNames.userLink} font-bold`} target="_blank"
                href={`${window.location.origin}/${redirectPage}/${sourceTicket.SourceTicketId}`}>
                #{threedigitized(sourceTicket.SourceTicketId)}
              </Link>
            </div>
            <div className="text-xs">
              <span className={`text-gray-400`}>{t("ticket-details.ticket-conversation.merged-by")}&nbsp;</span>
              <span>{mergedBy}&nbsp;</span>
              <span className={`text-gray-400`}>{t("ticket-details.ticket-conversation.on")}&nbsp;</span>
              <span>{getDateWithDay(sourceTicket.CreatedDate)}</span>
              <div>{t("ticket-details.ticket-conversation.all-attachments-and-comments-moved")}&nbsp;
                <span className={`font-bold ${classNames.TicketDetailHeader}`}>
                  #{threedigitized(sourceTicket.SourceTicketId)}
                </span>
              </div>
            </div>
          </> : <>
            <Skeleton className='mt-2' animation="pulse">
              <Flex gap='gap.small' column>
                <Skeleton.Line width="25%" />
                <Skeleton.Line width="40%" />
              </Flex>
            </Skeleton>
          </>}
        </div>
      </CheckFeature>
    </>;
  }

  // The folded line's right-hand end: "Sending...", a Send for a draft ready to go, or the plain arrow.
  const foldedSend = (colorScheme) => {
    if (commentState.isSending) {
      return <Text content={t("ticket-details.ticket-conversation.sending")} />;
    }
    if (draftPreview && !commentsDisabled()) {
      // Sends the draft as it stands, without opening the box first. Drawn as the open box's
      // Send button - white on its purple, darker on hover - so a draft ready to go looks
      // different from the empty line's plain arrow.
      return (
        <button type='button' className={AppCSS.DraftSend}
          title={t("ticket-details.ticket-conversation.send")}
          aria-label={t("ticket-details.ticket-conversation.send")}
          style={{
            '--draft-send': colorScheme.brand.background,
            '--draft-send-hover': colorScheme.brand.backgroundHover,
            color: colorScheme.brand.foreground4
          } as React.CSSProperties}
          onClick={(e) => { e.stopPropagation(); sendCommentCallback(); }}>
          <SendIcon />
        </button>
      );
    }
    // The colour of the open box's Send with nothing typed, so folding changes nothing.
    return <Text content={<SendIcon outline />} style={{ color: Props.isTicketClosed ? colorScheme.default.foregroundDisabled : colorScheme.default.foreground }} />;
  };

  const renderTicketDetails = () => {
    return (
      <FluentUIThemeConsumer render={({ siteVariables: { colorScheme } }) => (
        <>
          {commentState.isCommenting && Props.isEndUserPage && <Alert warning content={WARNING_NOTE} style={{ color: colorScheme.default.foreground, marginBottom: '1px', fontSize: '11.3px' }} />}
          <Flex className={`w-full print-hidden`}>
            <Flex.Item className={getTextBoxClass(commentState.isCommenting)}>
              <div className={`flex items-center justify-between gap-2 p-3 cursor-text w-full ${Props.isTicketClosed ? 'cursor-not-allowed' : ''}`}
                style={{ backgroundColor: getCommentBackgroundColor(colorScheme) }}
                ref={fakeBox}
                onClick={() => {
                  if (!Props.isTicketClosed) {
                    dispatch({ type: ACTIONS.COMMENTING_STATE_ON });
                  }
                }}>
                <div className='flex items-center gap-2 min-w-0' style={{ color: Props.isTicketClosed ? colorScheme.default.foregroundDisabled : colorScheme.default.foreground }}>
                  {draftPreview ? <>
                    <span className='font-semibold flex-shrink-0' style={{ color: colorScheme.brand.foreground1 }}>{t("ticket-details.ticket-conversation.draft")}</span>
                    <span className='truncate'>{draftPreview}</span>
                  </> : t("ticket-details.ticket-conversation.type-new-message")}
                </div>
                <div className='flex-shrink-0'>
                  {foldedSend(colorScheme)}
                </div>
              </div>
            </Flex.Item>
            <Flex.Item className={getTextBoxClass(!commentState.isCommenting)}>
              <div className={`w-full relative`} style={{ backgroundColor: getCommentBackgroundColor(colorScheme) }} ref={ref}>
                {commentState.isCommenting && <>
                  <div className={AppCSS.CommentBoxEdge}
                    style={{ cursor: resizeCursor(isResizingComposer ? colorScheme.brand.foreground1 : colorScheme.default.foreground) }}
                    title={t("ticket-details.ticket-conversation.resize-message")}
                    onPointerDown={onComposerResizeStart}
                    onPointerMove={onComposerResizeMove}
                    onPointerUp={onComposerResizeEnd}
                    onPointerCancel={onComposerResizeEnd}
                    onDoubleClick={onComposerResizeReset} />
                </>}
                <ShowEditor
                  teamId={Props.teamId}
                  groupId={Props.groupId}
                  onChange={onChangeShowEditorCallback}
                  isEditorVisible={commentState.isCommenting}
                  disabled={commentState.isSending}
                  commentBody={commentState?.body}
                  onImagePaste={pastedImageFile}
                  isEndUserPage={Props.isEndUserPage}
                  minHeight={commentBoxHeights.minHeight}
                  maxHeight={commentBoxHeights.maxHeight}
                  toolbarEnd={<>
                    {isWideScreen && <Button text primary iconOnly
                      icon={isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                      title={expandLabel}
                      aria-label={expandLabel}
                      aria-pressed={isExpanded}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setComposerExpanded(!isExpanded)} />}
                    <Button text primary iconOnly
                      icon={<ChevronDownIcon size="large" />}
                      title={t("ticket-details.ticket-conversation.collapse-message")}
                      aria-label={t("ticket-details.ticket-conversation.collapse-message")}
                      disabled={commentState.isSending}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => dispatch({ type: ACTIONS.COMMENTING_STATE_OFF })} />
                  </>} />
                <div className={`flex items-center overflow-auto ${droppedFiles.length > 0 ? 'm-2' : ''}`}>
                  {droppedFiles.map((file: any, index: number) => {
                    return (
                      <FileAttachments key={file.name} isUploading={file.isUploading} id={index} file={file} onRemoveFile={() => removeFile(index)} isRemoveDisabled={commentState?.isSending} isInProgress={file.isInProgress} percentComplete={file.progressPercent} />
                    )
                  })}
                </div>
                <Divider />
                <div className='md:flex justify-between'>
                  {!commentState.isSending && (
                    <>
                      {!Props.isEndUserPage && (<TicketQuickEdit
                        StatusId={Props.status}
                        DueDate={Props.dueDate}
                        teamId={Props.teamId}
                        groupId={Props.groupId}
                        AssigneeId={Props.assigneeId}
                        PriorityId={Props.priority}
                        onChange={onChangeTicketQuickEditCallback}
                        ticketData={Props.ticketData}
                        isUserLicensed={Props.isUserLicensed}
                        isLifecycleEnabled={isLifecycleEnabled}
                      />)}
                    </>
                  )}
                  <Flex className='flex-grow justify-between md:justify-end items-center md:px-0 px-1'>
                    <Flex.Item>
                      <div className="pb-1">
                        {!commentState.isSending && !Props.isEndUserPage &&
                          // The light-theme colours in both themes, by design: the dark theme's own
                          // brand purple and disabled grey washed out on this lavender.
                          <Button title={t("ticket-details.ticket-conversation.add-internal-note")} onClick={sendInternalNote} disabled={commentsDisabled()} primary className={`${AppCSS.InternalNote} ${AppCSS.NoFade}`} style={{ minWidth: "0px", padding: "0px", backgroundColor: "#E9EAF6", }}>
                            <div className='px-2 flex items-center text-xs font-semibold' style={{ color: commentsDisabled() ? '#C7C7C7' : '#6264A7' }}>
                              <LockSVG className='mr-2 text-private' />
                              <span className='leading-6 whitespace-nowrap'>{t("ticket-details.ticket-conversation.add-internal-note")}</span>
                            </div>
                          </Button>
                        }
                      </div>
                    </Flex.Item>
                    <Flex.Item>
                      <div className='px-2 pb-1'>
                        <div className={getCursorClass(commentState.body)}>
                          {commentState.isSending && <Text content={t("ticket-details.ticket-conversation.sending")} />}
                          {!commentState.isSending && <Button className={AppCSS.NoFade} title={t("ticket-details.ticket-conversation.send")} onClick={sendCommentCallback}
                            disabled={commentsDisabled()} text={commentsDisabled()} primary style={{ minWidth: "0px", width: "72px", padding: "0px" }}><SendIcon className='mr-1' />{t('ticket-details.ticket-conversation.send')}</Button>
                          }
                        </div>
                      </div>
                    </Flex.Item>
                  </Flex>
                </div>
              </div>
            </Flex.Item>
          </Flex>
        </>
      )} />
    )
  }

  const getTextBoxClass = (isCommenting: boolean) => {
    return isCommenting ? 'hidden' : '';
  }

  const getCursorClass = (body: string) => {
    return body === "" ? 'cursor-not-allowed' : '';
  }

  const getCommentBackgroundColor = (colorScheme: any) => {
    return Props.isTicketClosed ? colorScheme.default.backgroundDisabled : colorScheme.default.background;
  }

  const ref = useRef(null);
  const fakeBox = useRef(null);

  // Whose box height and expand choice these are. getUser decodes the signed-in token from
  // storage, so it is read once - the user does not change while the activity is open.
  const storageUser = useMemo(() => getUser(), []);
  const [draggedHeight, setDraggedHeight] = useState<number | null>(() => readDraggedHeight(storageUser));
  const [conversationHeight, setConversationHeight] = useState(0);
  const [composerChrome, setComposerChrome] = useState(0);
  const [lineMetrics, setLineMetrics] = useState({ lineHeight: 20, padding: 24 });
  // lastHeight is the height the latest move asked for. The drag's end saves that, not the
  // draggedHeight state: the last move's update may not have rendered when the pointer comes up,
  // and saving the state then stored the height from one move earlier.
  const composerDrag = useRef<{ startY: number; startHeight: number; lastHeight: number | null } | null>(null);
  const [isResizingComposer, setIsResizingComposer] = useState(false);
  const pressStartedInComposer = useRef(false);
  const [composerExpanded, setComposerExpandedState] = useState<boolean>(() => readExpanded(storageUser));
  // Expand is desktop only: a phone has too little height for it, and the box already
  // opens five lines tall. A choice remembered on desktop is ignored on a phone rather than
  // leaving an expanded box there with no button to restore it.
  const [isWideScreen, setIsWideScreen] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsWideScreen(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  const isExpanded = composerExpanded && isWideScreen;
  const setComposerExpanded = (expanded: boolean) => {
    setComposerExpandedState(expanded);
    saveExpanded(storageUser, expanded);
  };
  const expandLabel = isExpanded
    ? t("ticket-details.ticket-conversation.restore-message")
    : t("ticket-details.ticket-conversation.expand-message");
  const ceiling = conversationHeight > 0 ? activityCeiling(conversationHeight, composerChrome) : Number.POSITIVE_INFINITY;
  const commentBoxHeights = isExpanded
    ? expandedHeights(conversationHeight, lineMetrics.lineHeight, lineMetrics.padding, ceiling)
    : getCommentBoxHeights(conversationHeight, lineMetrics.lineHeight, lineMetrics.padding, draggedHeight, ceiling);
  const draftPreview = commentState.isMessageEmpty && droppedFiles.length === 0
    ? ''
    : draftPreviewText(commentState.body, droppedFiles.map((f: File) => f.name));

  // The conversation is the activity plus the composer, whose extent holds still as the
  // box grows - the activity gives up the space on desktop, and is overlaid on mobile.
  // Measured while the box is open, the only time the limit applies, rather than once on
  // mount: the activity is not in the page yet while the ticket is still loading.
  useEffect(() => {
    const chat = chatcontainer.current;
    const composer = chat?.parentElement?.querySelector<HTMLElement>('.comment-composer') ?? null;
    if (!commentState.isCommenting || !chat || !composer || typeof ResizeObserver === 'undefined') return undefined;

    // From the top of the activity to the bottom of the box. On desktop that is their sum;
    // on mobile the box is fixed over the activity, which does not shrink as the box grows,
    // so a sum would grow with the box and feed back into the next limit.
    const measure = () => {
      const box = composer.getBoundingClientRect();
      setConversationHeight(Math.round(Math.min(box.bottom, window.innerHeight) - chat.getBoundingClientRect().top));
      const editable = ref.current?.querySelector('.slate-editor-editable') as HTMLElement | null;
      if (editable) setComposerChrome(Math.round(box.height - editable.getBoundingClientRect().height));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(chat);
    observer.observe(composer);

    return () => observer.disconnect();
  }, [commentState.isCommenting]);

  useEffect(() => {
    if (!commentState.isCommenting) return;
    const editable = ref.current?.querySelector('.slate-editor-editable') as HTMLElement | null;
    if (!editable) return;

    const style = getComputedStyle(editable);
    const lineHeight = parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 14) * 1.5;
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    setLineMetrics(m => (m.lineHeight === lineHeight && m.padding === padding ? m : { lineHeight, padding }));
  }, [commentState.isCommenting]);

  const onComposerResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    const editable = ref.current?.querySelector('.slate-editor-editable') as HTMLElement | null;
    if (!editable) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setComposerExpanded(false);
    composerDrag.current = { startY: e.clientY, startHeight: editable.getBoundingClientRect().height, lastHeight: draggedHeight };
    setIsResizingComposer(true);
  };

  const onComposerResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!composerDrag.current) return;
    const { startY, startHeight } = composerDrag.current;
    const height = clampDraggedHeight(startHeight + startY - e.clientY, conversationHeight, lineMetrics.lineHeight, lineMetrics.padding, ceiling);
    composerDrag.current.lastHeight = height;
    setDraggedHeight(height);
  };

  const onComposerResizeEnd = () => {
    if (!composerDrag.current) return;
    const { lastHeight } = composerDrag.current;
    composerDrag.current = null;
    setIsResizingComposer(false);
    saveDraggedHeight(storageUser, lastHeight);
  };

  const onComposerResizeReset = () => {
    setDraggedHeight(null);
    saveDraggedHeight(storageUser, null);
  };

  // A press that starts in the box and is released outside it - selecting text, or
  // dragging the top edge - fires its click on whatever holds both points, which is
  // outside the box. That is not clicking away, so it must not fold the box.
  useEffect(() => {
    const onPress = (e: MouseEvent) => {
      pressStartedInComposer.current = !!ref.current?.contains(e.target);
    };
    document.addEventListener('mousedown', onPress, true);
    return () => document.removeEventListener('mousedown', onPress, true);
  }, []);

  // A comment that has not been sent asks before the ticket is left. The warning is raised by
  // the page - the agent's property form, which owns the router's single prompt, or the
  // requester's page - not here: this unmounts with the activity tab, and the draft it stashes
  // then must still be warned about. The page reads hasUnsentDraft, which covers both.
  const hasUnsentComment = !commentState.isMessageEmpty || droppedFiles.length > 0;
  useEffect(() => {
    setState(s => (s.hasUnsentComment === hasUnsentComment ? s : { ...s, hasUnsentComment }));
  }, [hasUnsentComment]);
  useEffect(() => () => setState(s => ({ ...s, hasUnsentComment: false })), []);

  function handleClickOutside(event) {
    // Buttons, dropdowns, lists, pop-ups and the properties panel; see composerClickAway.
    if (keepsComposerOpen(event.target) || event.target.tagName == 'svg') {
      return;
    }

    //if its searchbox then we can ignore this so search box can be clickable
    if (event.target.matches('input.ui-box')) {
      return;
    }

    // A link leaves the page or asks first; if the agent cancels, the box should be as it was.
    if (fakeBox.current.contains(event.target) || pressStartedInComposer.current || event.target.closest('a')) {
      return;
    }
    if (event.target.matches('a.mention-selection')) {
      return;
    }
    if (event.target.matches('div.mention-selection')) {
      return;
    }
    if (event.target.matches('mgt-person')) {
      return;
    }
    let calenderRef = document.querySelector('.ui-popup__content');

    if (ref.current && !ref.current.contains(event.target) && (calenderRef == null || !calenderRef.contains(event.target))) {
      if (!commentState.isSending) {
        dispatch({ type: ACTIONS.COMMENTING_STATE_OFF })
      }
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const onChangeShowEditor = (data: any) => {
    dispatch({ type: ACTIONS.TYPING, payload: { body: data } });
  };

  const onChangeShowEditorCallback = useCallback((data: any) => {
    onChangeShowEditor(data);
    dispatch({ type: isSlateBodyEmpty(data) ? ACTIONS.SET_IS_EMPTY : ACTIONS.SET_NOT_EMPTY });
  }, [onChangeShowEditor]);

  const onChangeTicketQuickEdit = (data) => {
    setToUpdate(data);
  };

  const onChangeTicketQuickEditCallback = useCallback((data) => { onChangeTicketQuickEdit(data) }, [onChangeTicketQuickEdit]);

  const removeFile = (index: number) => {
    setDroppedFiles(droppedFiles.filter((_df: File, i: number) => i !== index))
  }
  const pastedImageFile = (file: File) => {
    const result = new File([file], `image-${droppedIndexRef.current.toString().padStart(3, '0')}.png`, { type: file.type });
    setDroppedFiles((prev) => ([...prev, result]));
    setDroppedIndex((prevIndex) => prevIndex + 1);
  }

  const onConfirmSave = async () => {
    dispatch({ type: ACTIONS.COMMENTING_STATE_ON });

    //Need to assign the value because using only setToUpdate is delayed and won't make it before calling the update ticket.
    toUpdate.Resolution = updatedResolution;
    setToUpdate(
      {
        ...toUpdate,
        Resolution: updatedResolution,
      });

    setShowResolvedDialog(false);
    await sendCommentNow(true);
  }


  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <>
          <ResolutionNoteDialog
            resolutionProp={{ ticketData: Props.ticketData, resolutionNoteConfig: Props.resolutionNoteConfig } as IResolution}
            setShowDialog={setShowResolvedDialog}
            showDialog={showResolvedDialog}
            setUpdatedResolution={setUpdatedResolution}
            confirmSave={onConfirmSave}
          />
          <Flex.Item>
            <TicketConversationToolbarActivity
              isEndUserPage={Props.isEndUserPage}
              groupChat={Props.groupChat}
              circleImage={circleImage}
              groupChatText={groupChatText}
              loading={loading}
              switchComment={switchComment}
              properties={properties}
              filterIndex={filterIndex}
              searchQuery={searchQuery}
              isAutoFocused={isAutoFocused}
              setIsAutoFocused={setIsAutoFocused}
              onChangeHideHistory={onChangeHideHistoryCallbackWrapper}
              onChangefilterIndex={onChangefilterIndexCallbackWrapper}
              handleSearchClick={handleSearchClickCallbackWrapper}
              onSearch={onSearch}
            />
          </Flex.Item>
          {(commentState.loading || loading) && (
            <Flex.Item>
              <div className={`pt-5 mt-5`}>
                <Loader />
              </div>
            </Flex.Item>
          )}
          {emptySearch && (
            <Flex.Item className="print-hidden">
              <>
                <EmptyData
                  headerText={t("ticket-details.ticket-conversation.empty-result")}
                  subheaderText=""
                  SVGIcon={<EmptySVG width={100} height={100} className="mb-2" />}
                /></>
            </Flex.Item>
          )}
          <Flex.Item className="print-hidden">
            <div className={`chathistory-detail overflow-auto md:h-full w-full md:w-auto md:relative pb-20 md:pb-10 ${Props.isEndUserPage ? 'chathistory-enduser' : 'chathistory-analyst'} ${AppCSS.ChatContainer}`} ref={chatcontainer} onScroll={onScrollLoadMoreActivity}>
              <Chat items={commentState.conversation} />
            </div>
          </Flex.Item>
          <Flex.Item>
            <>
              <FileAlert show={isShowFileAlert} message={alertMessage} />
              <div  {...getRootProps({ className: 'dropzone h-full' })} className={`comment-composer md:mb-3 w-full bottom-0 left-0 z-10 fixed md:relative`}>
                {isDragActive && !commentState.isSending && (
                  <div className="w-full h-full absolute">
                    <div className='w-full h-full absolute left-0 top-0 z-0 opacity-50' style={{ zIndex: 2000, background: globalTheme.siteVariables.bodyBackground }}>
                    </div>
                    <Flex
                      style={{
                        border: `2px solid ${globalTheme.siteVariables.colorScheme.brand.foregroundActive}`,
                        zIndex: 2010
                      }}
                      className='left-0 top-0 absolute cursor-pointer w-full justify-center h-full'
                    >
                      <Text
                        content={t('ticket-details.drop-file-text')}
                        className="self-center"
                        style={{ color: globalTheme.siteVariables.colorScheme.brand.foregroundActive }}
                      />
                    </Flex>
                  </div>
                )}
                {Props.isTicketMerged ? renderMergedTicketDetails() : renderTicketDetails()}
              </div>
            </>
          </Flex.Item>
        </>
      )} />
  );
};
